import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { IMG } from "@/lib/site";
import PressBar from "@/components/site/PressBar";
import { useT } from "@/lib/i18n";

const HomeHero = () => {
  const t = useT();
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-sand/50 via-ivory to-ivory" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-12 md:pt-20 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-7" data-testid="hero-overline">
              {t("home.eyebrow")}
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-charcoal">
              {t("brand.first")} <span className="italic text-terracotta">TAO</span>
              <span className="block text-charcoal/80 text-3xl md:text-4xl lg:text-5xl mt-3 italic font-light">
                by Marta
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-charcoal/75 font-light">
              {t("home.hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/sesion-gratuita"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-8 py-4 text-sm font-medium tracking-wide hover:bg-terracotta transition-all"
              >
                {t("nav.freeSession")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/metodo-tao"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-charcoal/25 text-charcoal rounded-full px-8 py-4 text-sm font-medium tracking-wide hover:border-charcoal hover:bg-charcoal/5 transition-all"
              >
                {t("home.method.cta")}
              </Link>
            </div>
            <PressBar />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-terracotta/20 blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full bg-sage/20 blur-3xl" />
              <div className="watermark-hide-ivory relative aspect-[4/5] rounded-tr-[5rem] rounded-bl-[5rem] overflow-hidden bg-sand">
                <img
                  src={IMG.heroPortrait}
                  alt="Marta Montardit, creadora del Método TAO"
                  className="w-full h-full object-cover object-[center_35%] scale-105"
                  data-testid="hero-portrait"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-6 left-6 md:left-10 bg-ivory border border-sand/70 rounded-2xl px-5 py-4 shadow-xl shadow-charcoal/5 max-w-[260px]">
                <p className="font-serif italic text-charcoal text-base leading-snug">
                  {t("home.heroQuote")}
                </p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-charcoal/50 mt-2">{t("home.heroQuoteAuthor")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
